import * as React from 'react';
import { styled } from '@mui/material/styles';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import moment, { Moment } from "moment";

interface CustomPickerDayProps extends PickersDayProps {
    isSelected: boolean;
    isHovered: boolean;
}

const CustomPickersDay = styled(PickersDay, {
    shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'isHovered',
})<CustomPickerDayProps>(({ theme, isSelected, isHovered, day }) => ({
    borderRadius: 0,
    ...(isSelected && {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        '&:hover, &:focus': {
            backgroundColor: theme.palette.primary.main,
        },
    }),
    ...(isHovered && {
        backgroundColor: theme.palette.primary[theme.palette.mode],
        '&:hover, &:focus': {
            backgroundColor: theme.palette.primary[theme.palette.mode],
        },
    }),
    ...(day.day() === 1 && {
        borderTopLeftRadius: '50%',
        borderBottomLeftRadius: '50%',
    }),
    ...(day.day() === 0 && {
        borderTopRightRadius: '50%',
        borderBottomRightRadius: '50%',
    }),
})) as React.ComponentType<CustomPickerDayProps>;

const isInSameWeek = (dayA: moment.Moment, dayB: moment.Moment | null | undefined) => {
    if (!dayB) return false;
    return dayA.isoWeek() === dayB.isoWeek() && dayA.isoWeekYear() === dayB.isoWeekYear();
};

function Day(
    props: PickersDayProps & {
        selectedDay?: Moment | null;
        hoveredDay?: Moment | null;
    },
) {
    const { day, selectedDay, hoveredDay, ...other } = props;

    return (
        <CustomPickersDay
            {...other}
            day={day}
            sx={{ px: 2.5 }}
            disableMargin
            selected={false}
            isSelected={isInSameWeek(day, selectedDay)}
            isHovered={isInSameWeek(day, hoveredDay)}
        />
    );
}

interface IWeekPickerProps {
    startOfWeek: Moment | null
    onDateChange: (newValue: Moment) => void
}

export default function WeekPicker({ startOfWeek, onDateChange }: IWeekPickerProps) {
    const [hoveredDay, setHoveredDay] = React.useState<Moment | null>(null);

    return (
        <DateCalendar
            value={startOfWeek}
            onChange={(newValue: moment.Moment) => onDateChange(newValue)}
            showDaysOutsideCurrentMonth
            displayWeekNumber
            disableFuture
            slots={{ day: Day }}
            slotProps={{
                day: (ownerState) =>
                ({
                    selectedDay: startOfWeek,
                    hoveredDay,
                    onPointerEnter: () => setHoveredDay(ownerState.day),
                    onPointerLeave: () => setHoveredDay(null),
                } as any),
            }}
        />
    );
}